import clsx from 'clsx'
import map from 'lodash/map'
import takeRight from 'lodash/takeRight'
import React, { ComponentProps, memo } from 'react'
import type { BreadcrumbList, WithContext } from 'schema-dts'

import AngleRight from '@Assets/SVG/AngleRight'
import { Link } from '@Components/Link'
import { MetadataContext } from '@Globals/Metadata'
import { stringToPageType } from '@Globals/Page/Mappers'

import styles from './Breadcrumbs.module.scss'

export const Breadcrumbs = memo(({
  className,
  maxElements,
  withMaxWidth = false,
  ...props
}: {
  maxElements?: number
  withMaxWidth?: boolean
} & ComponentProps<'div'>) => {
  const { page: { breadcrumbs = [], id = '', type = '' } = {} } = React.useContext(MetadataContext)
  if (!breadcrumbs || breadcrumbs.length === 0) {
    return null
  }

  const schema: WithContext<BreadcrumbList> = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.title,
      item: crumb.url || '',
    })),
    itemListOrder: 'http://schema.org/ItemListOrderAscending',
    numberOfItems: breadcrumbs.length,
  }

  const scriptTag = (
    <script dangerouslySetInnerHTML={{ __html: JSON.stringify(schema).replace(/</g, '\\u003c') }} type="application/ld+json" />
  )

  if (breadcrumbs.length === 1
    && stringToPageType(breadcrumbs[0].type || '') === stringToPageType(type)
    && breadcrumbs[0].id === id
  ) {
    return scriptTag
  }

  return (
    <div
      className={clsx(
        className,
        styles.wrapper,
        withMaxWidth && styles.maxWidth,
      )}
      {...props}
    >
      <ol className={styles.breadcrumbs}>
        {map(takeRight(breadcrumbs, maxElements || breadcrumbs.length), (breadcrumb) => {
          let content = (
            <span>{breadcrumb.title}</span>
          )

          if (breadcrumb.url) {
            content = (
              <Link
                analytics={{
                  dest_page_type: breadcrumb.type,
                  dest_page_type_id: breadcrumb.id,
                  source_id: 'breadcrumb',
                }}
                href={breadcrumb.url || '/'}
              >
                {content}
              </Link>
            )
          }

          return (
            <li key={`${breadcrumb.type}-${breadcrumb.id}`} className={clsx('breadcrumb', styles.breadcrumb)}>
              <AngleRight className={styles.angle} />

              {content}
            </li>
          )
        })}
      </ol>
      {scriptTag}
    </div>
  )
})
